import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { UploadCloud, Link2, FileText, FileSpreadsheet, File, Globe, Trash2, Loader2, Sparkles } from "lucide-react";
import { knowledge } from "@/lib/api";
import { toast } from "sonner";

const ACCEPT = ".pdf,.docx,.xlsx,.xlsm,.csv,.txt,.md";

function iconFor(doc) {
  if (doc.kind === "link") return Globe;
  const t = (doc.original_filename || doc.title || "").toLowerCase();
  if (t.endsWith(".pdf")) return FileText;
  if (t.endsWith(".xlsx") || t.endsWith(".xlsm") || t.endsWith(".csv")) return FileSpreadsheet;
  if (t.endsWith(".docx") || t.endsWith(".txt") || t.endsWith(".md")) return FileText;
  return File;
}

// Shared uploader + knowledge list for both Associate Training and the
// company About page. `scope` is "associate" | "company".
export function KnowledgeManager({ scope, associateId, disabled = false }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [url, setUrl] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await knowledge.list(scope, associateId);
      setItems(data.items || []);
    } catch (_) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, associateId]);

  const onFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    for (const f of files) {
      setProgress(0);
      try {
        await knowledge.upload(f, scope, associateId, setProgress);
        toast.success(`Added “${f.name}”`, { description: "Reading and learning from it now." });
      } catch (e) {
        const msg = e?.response?.data?.detail || "Upload failed";
        toast.error(`Couldn't add “${f.name}”`, { description: msg });
      }
    }
    setBusy(false);
    setProgress(0);
    if (fileRef.current) fileRef.current.value = "";
    load();
  };

  const addLink = async () => {
    const u = url.trim();
    if (!u) return;
    setBusy(true);
    try {
      await knowledge.addLink(u, scope, associateId);
      toast.success("Link added", { description: "Read and summarized for the team." });
      setUrl("");
      load();
    } catch (e) {
      toast.error("Couldn't read that link", { description: e?.response?.data?.detail || "Try another URL" });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await knowledge.remove(id);
      setItems((x) => x.filter((d) => d.id !== id));
      toast.success("Removed");
    } catch (_) {
      toast.error("Couldn't remove");
    }
  };

  return (
    <div className="space-y-5" data-testid="knowledge-manager">
      {/* Upload dropzone */}
      <div
        onDragOver={(e) => { e.preventDefault(); }}
        onDrop={(e) => { e.preventDefault(); if (!disabled) onFiles(e.dataTransfer.files); }}
        className={`rounded-2xl border border-dashed p-6 text-center transition-colors ${disabled ? "border-white/10 opacity-50" : "border-[#00E5FF]/30 hover:border-[#00E5FF]/60"}`}
        data-testid="knowledge-dropzone"
      >
        <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" disabled={disabled} onChange={(e) => onFiles(e.target.files)} data-testid="knowledge-file-input" />
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-[#00E5FF]/10">
          {busy ? <Loader2 className="animate-spin text-[#00E5FF]" size={22} /> : <UploadCloud className="text-[#00E5FF]" size={22} />}
        </div>
        <div className="mt-3 text-sm text-white">{busy ? `Uploading… ${progress}%` : "Drop files here, or"}</div>
        <button
          onClick={() => fileRef.current && fileRef.current.click()}
          disabled={disabled || busy}
          className="mt-2 rounded-lg bg-[#00E5FF] px-4 py-2 text-sm font-semibold text-[#030A16] transition-transform hover:scale-[1.02] disabled:opacity-50"
          data-testid="knowledge-browse-btn"
        >
          Choose files
        </button>
        <div className="mt-2 text-[11px] text-slate-500">PDF, Word (.docx), Excel/CSV (.xlsx/.csv), text — up to 15MB each</div>
      </div>

      {/* Add link */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Link2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addLink()}
            placeholder="Paste a website link (e.g. your homepage or a help doc)"
            disabled={disabled || busy}
            className="h-11 w-full rounded-xl border border-white/10 bg-[#030A16]/60 pl-9 pr-3 text-sm text-white placeholder:text-slate-500 focus:border-[#00E5FF]/50"
            data-testid="knowledge-link-input"
          />
        </div>
        <button
          onClick={addLink}
          disabled={disabled || busy || !url.trim()}
          className="h-11 shrink-0 rounded-xl border border-white/10 px-4 text-sm text-slate-200 transition-colors hover:border-[#00E5FF]/40 hover:text-white disabled:opacity-40"
          data-testid="knowledge-add-link-btn"
        >
          Add link
        </button>
      </div>

      {/* List */}
      <div className="space-y-2" data-testid="knowledge-list">
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-slate-500"><Loader2 className="animate-spin" size={15} /> Loading knowledge…</div>
        ) : items.length === 0 ? (
          <div className="rounded-xl border border-white/8 bg-[#030A16]/40 p-6 text-center text-sm text-slate-500" data-testid="knowledge-empty">
            Nothing added yet. Upload documents or paste links above to build this knowledge.
          </div>
        ) : (
          items.map((d) => {
            const Icon = iconFor(d);
            return (
              <motion.div
                key={d.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-3 rounded-xl border border-white/8 bg-[#030A16]/40 p-3.5"
                data-testid={`knowledge-item-${d.id}`}
              >
                <span className="mt-0.5 grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white/5 text-[#00E5FF]"><Icon size={16} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="truncate text-sm font-medium text-white">{d.title}</div>
                    <span className="shrink-0 rounded-full border border-white/10 px-1.5 py-0.5 text-[10px] font-mono uppercase text-slate-400">{d.kind === "link" ? "link" : (d.original_filename || "").split(".").pop()}</span>
                  </div>
                  {d.url && <a href={d.url} target="_blank" rel="noreferrer" className="block truncate text-[11px] text-[#00E5FF]/80 hover:underline">{d.url}</a>}
                  {d.summary ? (
                    <div className="mt-1 flex items-start gap-1.5 text-xs text-slate-400">
                      <Sparkles size={12} className="mt-0.5 shrink-0 text-[#8A2BE2]" />
                      <span className="line-clamp-3">{d.summary}</span>
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">Stored as reference knowledge.</div>
                  )}
                </div>
                <button onClick={() => remove(d.id)} className="shrink-0 rounded-lg p-2 text-slate-500 transition-colors hover:bg-[#FF0055]/10 hover:text-[#FF3B5C]" aria-label="Remove" data-testid={`knowledge-remove-${d.id}`}>
                  <Trash2 size={15} />
                </button>
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
}
